/**
 * PriceOverrideModal (S2.2) — manual unit-price override on an SO line.
 *
 * Composes the shared `ReasonRequiredModal` (its first consumer): the caller
 * gets a new transacted price ONLY together with a mandatory business reason
 * (override without reason is blocked — the anti-175% guard). A deviation
 * beyond ±SO_PRICE_DEVIATION_THRESHOLD_PCT previews the 價格審核
 * pending-approval hold (blocks fulfillment/invoicing, NOT entry).
 */

import { useEffect, useState } from 'react';
import { Alert, InputNumber, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useFormStyles } from '@/styles/formStyles';
import { roundHalfUp2 } from '@/lib/pricing';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';
import { formatMoney } from './soFormat';

const { Text } = Typography;

export interface PriceOverrideModalProps {
  open: boolean;
  /** "SKU — display name" of the line being overridden. */
  productLabel: string;
  currency: string;
  /** System-resolved price; null = unresolvable (manual price still allowed). */
  resolvedUnitPrice: number | null;
  /** Current transacted price (pre-fills the input). */
  currentUnitPrice: number | null;
  onCancel: () => void;
  onApply: (unitPrice: number, reason: string) => void;
}

const PriceOverrideModal = ({
  open,
  productLabel,
  currency,
  resolvedUnitPrice,
  currentUnitPrice,
  onCancel,
  onApply,
}: PriceOverrideModalProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();

  const [price, setPrice] = useState<number | null>(null);
  const [priceMissing, setPriceMissing] = useState(false);

  useEffect(() => {
    if (open) {
      setPrice(currentUnitPrice ?? resolvedUnitPrice);
      setPriceMissing(false);
    }
  }, [open, currentUnitPrice, resolvedUnitPrice]);

  const deviationPct =
    price !== null && resolvedUnitPrice !== null && resolvedUnitPrice !== 0
      ? roundHalfUp2((Math.abs(price - resolvedUnitPrice) / resolvedUnitPrice) * 100)
      : null;
  const beyondThreshold = deviationPct !== null && deviationPct > SO_PRICE_DEVIATION_THRESHOLD_PCT;

  const handleSubmit = (reason: string): boolean => {
    if (price === null || price <= 0) {
      setPriceMissing(true);
      return false; // keep the modal open — caller-side validation failed
    }
    onApply(roundHalfUp2(price), reason);
    return true;
  };

  return (
    <ReasonRequiredModal
      open={open}
      title={t('orders:so.override.title')}
      submitText={t('orders:so.override.apply')}
      cancelText={t('common:actions.cancel')}
      reasonLabel={t('orders:so.override.reasonLabel')}
      requiredMessage={t('orders:so.override.reasonRequired')}
      reasonPlaceholder={t('orders:so.override.reasonPlaceholder')}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <div style={formStyles.fieldContainerStyle}>
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {productLabel}
        </Text>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('orders:so.override.systemPrice')}
          </Text>
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
            {resolvedUnitPrice !== null
              ? formatMoney(currency, resolvedUnitPrice)
              : t('orders:so.trace.unresolved')}
          </Text>
        </div>
      </div>

      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {t('orders:so.override.newPrice')}
      </Text>
      <InputNumber
        value={price}
        onChange={(value) => {
          setPrice(value);
          if (value !== null && value > 0) setPriceMissing(false);
        }}
        min={0.01}
        precision={2}
        size="large"
        style={{ ...formStyles.inputStyle, marginBottom: '4px' }}
        status={priceMissing ? 'error' : undefined}
        addonBefore={currency}
      />
      {priceMissing && (
        <Text style={formStyles.errorStyle}>{t('orders:so.override.priceRequired')}</Text>
      )}

      {beyondThreshold && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:so.override.pendingApprovalWarning', {
            pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
          })}
          style={{ marginTop: token.marginSM, marginBottom: token.marginSM }}
        />
      )}
      <div style={{ height: token.marginMD }} />
    </ReasonRequiredModal>
  );
};

export default PriceOverrideModal;
